import { memo, useEffect, useRef, useState } from 'react'
import { AnswerData, PostAnswerData, McqGameInputData } from '../../models/global.types'
import { Button, Text } from '../atoms'
import { ButtonTypes } from '../atoms/Button/Button'
import ControlHighlight from '../molecules/KeyboardControls/ControlHighlight'
import { HighlightEffect } from '../../enums/KeyboardControls'
import { kbControlsLayerDefault, useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import KeyboardControl from '../molecules/KeyboardControls/KeyboardControl'
import { useMcqSoundFx } from '../../hooks/useMcqSoundFx'

export enum FocusableElement {
  ButtonContinue = 'button-continue',
  ButtonGo = 'button-go',
  ButtonReset = 'button-reset',
  InputAnswer = 'input-answer',
  QuestionText = 'question-text'
}

interface AccessibleMultipleChoiceGameProps {
  onEndGame: (questionsAndAnswersData: PostAnswerData[]) => Promise<void> | void
  onUpdateXP: (questionAndAnswersData: PostAnswerData[]) => void
  questionsData: McqGameInputData
  /** Fires when a submitted answer is not correct (earns no XP). */
  onIncorrectAnswer?: () => void
  /** Fires when CONTINUE is pressed, right before advancing/ending the game. */
  onContinue?: () => void
}

const AccessibleMultipleChoiceGame: React.FC<AccessibleMultipleChoiceGameProps> = ({ onEndGame, onUpdateXP, questionsData, onIncorrectAnswer, onContinue }: AccessibleMultipleChoiceGameProps) => {
  const [answers, setAnswers] = useState<AnswerData[]>([]) // Used to track answers for a specific question.  Will be cleared after each question is answered.
  const [questionAndAnswers, setQuestionsAndAnswers] = useState<PostAnswerData[]>([]) // Used to track overall answer for questions.  Will be send back to implementing component.
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0)
  const [totalScore, setTotalScore] = useState<number>(questionsData.totalScore)
  // All three buttons stay mounted throughout (per the ticket, so the accessible layout doesn't keep
  // changing which controls are on screen). awaitingContinue drives which of them are enabled.
  const [awaitingContinue, setAwaitingContinue] = useState<boolean>(false)
  const checkboxesRef = useRef<HTMLInputElement[]>([])
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const keyboardControls = useKeyboardControls()
  const soundFx = useMcqSoundFx(questionsData.levelName)

  // The up/down arrow keys scroll the question/answers area (they are not used for
  // selecting answers - answers are toggled with Tab + Space/Enter). Guarded to the
  // default layer so the arrows don't scroll behind an open modal/overlay.
  useEffect(() => {
    const handleArrowKeyScroll = (event: KeyboardEvent): void => {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
      if (keyboardControls.activeLayer !== kbControlsLayerDefault) return
      const scrollContainer = scrollContainerRef.current
      if (scrollContainer === null) return
      event.preventDefault()
      scrollContainer.scrollBy({ top: event.key === 'ArrowDown' ? 40 : -40 })
    }

    document.addEventListener('keydown', handleArrowKeyScroll)
    return () => document.removeEventListener('keydown', handleArrowKeyScroll)
  }, [keyboardControls.activeLayer])

  const answersEqual = (answers1: AnswerData[], answers2: AnswerData[]): boolean => {
    if (answers1.length !== answers2.length) return false

    // Sort both arrays before comparing
    const sortedAnswers1 = [...answers1].sort((a, b) => a.id.localeCompare(b.id))
    const sortedAnswers2 = [...answers2].sort((a, b) => a.id.localeCompare(b.id))

    // Compare each element
    return sortedAnswers1.every((value, index) => value.id === sortedAnswers2[index].id)
  }

  const resetGame = (): void => {
    setAnswers([])
    keyboardControls.setFocusedElement(FocusableElement.QuestionText)
  }

  const handleAnswerChange = (answer: AnswerData): void => {
    let updatedAnswers
    if (answers.find(a => a.id === answer.id) != null) { // Remove answer if the user unchecked the box:
      updatedAnswers = answers.filter(a => a.id !== answer.id)
    } else { // Add the answer if the user checked the box:
      soundFx.playOptionSelect()
      updatedAnswers = [...answers]
      const newAnswer: AnswerData = {
        id: answer.id,
        is_correct: answer.is_correct,
        text: answer.text
      }
      updatedAnswers.push(newAnswer)
    }
    setAnswers(updatedAnswers)
  }

  const handleGoButtonClick = (): void => {
    soundFx.playSubmit()

    let newTotalScore = totalScore
    const newQuestionsAndAnswers = [...questionAndAnswers]

    // Actually get all correct answers and compare them.
    const correctAnswers = questionsData.questions[currentQuestionIndex].answers.filter(a => a.is_correct)

    const allAnswersAreCorrect = answersEqual(correctAnswers, answers)

    if (!allAnswersAreCorrect) {
      // No XP earned - let the parent show wrong-answer feedback (e.g. Dax's speech bubble).
      onIncorrectAnswer?.()
      soundFx.playWrongAnswer()
    } else {
      soundFx.playRightAnswer()

      newTotalScore += 3
      // We only pass back correctly answered questions to onUpdateXP.
      const answeredQuestion: PostAnswerData = {
        question_id: questionsData.questions[currentQuestionIndex].id,
        answers: answers.map(a => a.id)
      }

      newQuestionsAndAnswers.push(answeredQuestion)
      setQuestionsAndAnswers(newQuestionsAndAnswers)
      setTotalScore(newTotalScore)
      onUpdateXP([answeredQuestion])
    }

    // Hold here (rather than progressing straight away) so CONTINUE becomes the only enabled action
    // until the player is ready to move on.
    setAwaitingContinue(true)
  }

  const handleContinueButtonClick = (): void => {
    onContinue?.()

    if ((currentQuestionIndex === questionsData.questions.length - 1) || (totalScore >= 100)) {
      // End game here
      void onEndGame(questionAndAnswers)
    } else {
      // Progress the game
      setCurrentQuestionIndex(currentQuestionIndex + 1)
      resetGame()
    }
    setAwaitingContinue(false)
  }

  const handleResetButtonClick = (): void => {
    soundFx.playReset()

    // Uncheck all checkboxes
    checkboxesRef.current.forEach(checkbox => {
      checkbox.checked = false
    })

    resetGame()
  }

  const answerComponents = questionsData.questions[currentQuestionIndex].answers.map((answer, index: number) => {
    return (
      <div className='justify-between flex-grow pt-2 sm:pt-4 md:pt-6 pr-[33.333%]' key={index}>
        <label className='flex h-full items-center'>
          <KeyboardControl>
            <input
              id={`${FocusableElement.InputAnswer}-${index}`}
              key={answer.id}
              onChange={_event => handleAnswerChange(answer)}
              ref={(el: HTMLInputElement) => (checkboxesRef.current[index] = el)}
              type='checkbox'
              value={answer.text}
            />
          </KeyboardControl>
          <ControlHighlight
            active={keyboardControls.isElementFocused(`${FocusableElement.InputAnswer}-${index}`)}
            highlightEffect={HighlightEffect.LinkUnderline}
            key={index}
          >
            <Text className='font-bold pl-4 text-white' size='LG' text={answer.text} />
          </ControlHighlight>
        </label>
      </div>
    )
  })

  return (
    <div className='flex flex-col h-[90vh] px-24'>
      <div ref={scrollContainerRef} className='flex-grow min-h-0 overflow-y-auto pt-8'>
        <div className='flex flex-col'>
          <KeyboardControl>
            <Text
              className='border font-bold flex text-dopage-primary'
              id={FocusableElement.QuestionText}
              size='XL'
              tabIndex={0}
              text={questionsData.questions[currentQuestionIndex].text}
            />
          </KeyboardControl>
          {answerComponents}
        </div>
      </div>
      {/* All three buttons stay on screen for the whole quiz - per the ticket, the accessible view
          should not keep changing which controls are present the way the standard view does. Which
          ones are actionable at a given moment is communicated by disabling them instead. */}
      <div className='justify-center pt-8 pb-8 flex shrink-0'>
        <ControlHighlight
          active={keyboardControls.isElementFocused(FocusableElement.ButtonReset)}
          className='h-[80px] mr-4'
          highlightEffect={HighlightEffect.ButtonHexOutline}
        >
          <KeyboardControl disabled={awaitingContinue}>
            <Button buttonType={ButtonTypes.secondary} disabled={awaitingContinue} id={FocusableElement.ButtonReset} onClick={handleResetButtonClick} text='RESET' />
          </KeyboardControl>
        </ControlHighlight>
        <ControlHighlight
          active={keyboardControls.isElementFocused(FocusableElement.ButtonGo)}
          className='h-[80px] mx-4'
          highlightEffect={HighlightEffect.ButtonHexOutline}
        >
          <KeyboardControl disabled={awaitingContinue}>
            <Button disabled={awaitingContinue} onClick={handleGoButtonClick} id={FocusableElement.ButtonGo} wrapperClass='w-[210px]' className='diamond-btn h-[80px] w-full font-bold flex justify-center items-center' type='submit'>
              <span className='text-black text-[26px] text-neutral-090'>GO</span>
            </Button>
          </KeyboardControl>
        </ControlHighlight>
        <ControlHighlight
          active={keyboardControls.isElementFocused(FocusableElement.ButtonContinue)}
          className='h-[80px] ml-4'
          highlightEffect={HighlightEffect.ButtonHexOutline}
        >
          <KeyboardControl disabled={!awaitingContinue}>
            <Button disabled={!awaitingContinue} onClick={handleContinueButtonClick} id={FocusableElement.ButtonContinue} wrapperClass='w-[210px]' className='diamond-btn h-[80px] w-full font-bold flex justify-center items-center' type='button'>
              <span className='text-black text-[26px] text-neutral-090'>CONTINUE</span>
            </Button>
          </KeyboardControl>
        </ControlHighlight>
      </div>
    </div>
  )
}

export default memo(AccessibleMultipleChoiceGame)
